import Http from '@/services/http'
import { URLRedirect } from '@/types/onlineStore'
import { PLATFORM, SHOP_DOMAIN } from '@/utils'
import { buildQueryString } from '@/utils/http'
import {
  Product,
  Collection,
  NullOrType,
  Cart,
  Checkout,
  ProductDescription,
  ProductShipping,
  GetProductReviewsParams,
  Page,
  RequestTracking,
  RequestKolCampaignTracking,
  CollectionAndProduct,
  CollectionAndProducts,
  Recommendation,
  SettingsType,
  LandingPage,
} from '@/types'
import {
  CheckoutAttributesUpdateInput,
  CheckoutDiscountCodeApplyInput,
  CheckoutDiscountCodeRemoveInput,
  CheckoutEmailUpdateInput,
  CheckoutLineItemsAddInput,
  CheckoutLineItemsRemoveInput,
  CheckoutLineItemsUpdateInput,
  CompleteCheckoutInput,
  CreateCartInput,
  CartLinesAddInput,
  CartLinesUpdateInput,
  CartBuyerIdentityUpdateInput,
  CartDiscountCodesUpdateInput,
  CreateCheckoutInput,
  CustomerAccessTokenCreateInput,
  CustomerActivateInput,
  CustomerCreateInput,
  CustomerResetInput,
  GetCollectionParam,
  JudgemeCustomFormQuestionResponse,
  JudgemeGetCustomQuestionInput,
  JudgeMeGetProductReviewsParams,
  JudgeMeGetProductReviewsResult,
  JudgemePrerequisiteRequestForUploadResponse,
  ProductRecommendationResult,
  UpdateCheckoutShippingAddressInput,
  UpdateCheckoutShippingLineInput,
} from '../type'
import { mappingProductRecommendationResultToProduct } from '@/utils/product'

const APIAuthorization = 'Bearer 5|dkxtKI5ubKgOoxlARzLnHmYmuZMrfWNFDjlyrtG6'
export abstract class Provider {
  abstract getPageByHandle(handle: string): Promise<Page>
  abstract getArticleBlogByHandle(
    blogHandle: string,
    articleHandle: string
  ): Promise<any>
  abstract getListArticleBlogByHandle(param: {
    handle: string
    numberOfArticles?: number
    after?: string
    before?: string
  }): Promise<any>
  abstract getProductById(id: string): Promise<Product>
  abstract getProductByHandle(id: string): Promise<Product>
  async getSettings(): Promise<Array<SettingsType>> {
    const query = encodeURIComponent(
      `*[!(_id in path('drafts.**')) && (_type == "settings" || _type == "popupSettings" || _type == "abTestSetting" || _type == "styleSettings" || _type == "scriptSettings" || _type == "productPageSettings" || _type == "productLandingPageSettings" || _type == "campaignsDiscountSettings")]`
    )
    const url = import.meta.env.VITE_SETTING_DATA_END_POINT as string
    const response = await Http.get(url + query)
    return response.result
  }
  async getBaseSettings(): Promise<Array<SettingsType>> {
    const query = encodeURIComponent(
      `*[!(_id in path('drafts.**')) && (_type == "settings" || _type == "productPageSettings" || _type == "campaignsDiscountSettings")]`
    )
    const url = import.meta.env.VITE_BASE_SETTING_DATA_END_POINT as string
    const response = await Http.get(url + query)
    return response.result
  }
  async getLandingPageByHandle(handle: string): Promise<LandingPage> {
    const query = encodeURIComponent(
      `*[!(_id in path('drafts.**')) && _type=="landingPageSettings"].landingPages[handle match "${handle}"]`
    )
    const url = import.meta.env.VITE_SETTING_DATA_END_POINT as string
    const response = await Http.get(url + query)
    return response.result[0]
  }
  abstract getCollectionByHandle(
    param: GetCollectionParam
  ): Promise<NullOrType<Collection>>
  abstract getCollectionByHandleAndProductByIds(
    param: GetCollectionParam
  ): Promise<CollectionAndProducts>
  abstract getCollectionByHandleAndProductByHandle(
    param: GetCollectionParam
  ): Promise<CollectionAndProduct>
  abstract createCart(input: CreateCartInput): Promise<Cart>
  abstract getCart(cartID: string): Promise<Cart>
  abstract cartLinesAdd(input: CartLinesAddInput): Promise<Cart>
  abstract cartLinesUpdate(input: CartLinesUpdateInput): Promise<Cart>
  abstract cartBuyerIdentityUpdate(
    input: CartBuyerIdentityUpdateInput
  ): Promise<Cart>
  abstract cartDiscountCodesUpdate(
    input: CartDiscountCodesUpdateInput
  ): Promise<Cart>
  abstract cartAttributesUpdate(input: any): Promise<Cart>
  abstract createCheckout(input: CreateCheckoutInput): Promise<Checkout>
  abstract getCheckout(checkoutId: string): Promise<Checkout>
  abstract checkoutLineItemsAdd(
    input: CheckoutLineItemsAddInput
  ): Promise<Checkout>
  abstract checkoutLineItemsRemove(
    input: CheckoutLineItemsRemoveInput
  ): Promise<Checkout>
  abstract checkoutLineItemsUpdate(
    input: CheckoutLineItemsUpdateInput
  ): Promise<Checkout>
  abstract checkoutAttributesUpdate(
    input: CheckoutAttributesUpdateInput
  ): Promise<Checkout>
  abstract checkoutDiscountCodeApply(
    input: CheckoutDiscountCodeApplyInput
  ): Promise<{ checkout: Checkout; checkoutErrors: any }>
  abstract checkoutDiscountCodeRemove(
    input: CheckoutDiscountCodeRemoveInput
  ): Promise<Checkout>
  abstract checkoutEmailUpdate(
    input: CheckoutEmailUpdateInput
  ): Promise<Checkout>

  abstract updateCheckoutShippingAddress(
    input: UpdateCheckoutShippingAddressInput
  ): Promise<Checkout>
  abstract updateCheckoutShippingLine(
    input: UpdateCheckoutShippingLineInput
  ): Promise<Checkout>
  abstract completeCheckout(input: CompleteCheckoutInput): Promise<Checkout>
  abstract completeFreeCheckout(checkoutId: string): Promise<Checkout>

  abstract createCustomer(input: CustomerCreateInput): Promise<any>
  abstract createCustomerAccessToken(
    input: CustomerAccessTokenCreateInput
  ): Promise<any>
  abstract getCustomer(customerAccessToken: string): Promise<any>
  abstract recoverCustomer(email: string): Promise<any>
  abstract resetCustomer(id: string, input: CustomerResetInput): Promise<any>
  abstract activeCustomer(
    id: string,
    input: CustomerActivateInput
  ): Promise<any>
  abstract createCustomerAddress(
    address: MailingAddressInput,
    customerAccessToken: string
  ): Promise<any>
  abstract updateCustomerAddress(
    address: MailingAddressInput,
    customerAccessToken: string,
    id: string
  ): Promise<any>
  abstract getLocalization(): Promise<any>
  abstract getProductRecommendations(id: string): Promise<any>

  abstract getURLRedirects(path: string): Promise<URLRedirect[]>

  async getKeyCount(key: string): Promise<number> {
    if (!key) return Promise.resolve(0)
    const url = `https://webhook.silverybrand.com/api/facebook/sale-id`
    const response = await Http.post(
      url,
      JSON.stringify({
        id: key,
      })
    )
    return response.count
  }

  async uploadImageBase64(base64: string): Promise<string> {
    if (!base64) return ''
    const url = `https://webhook.silverybrand.com/api/upload-base64`
    const formData = new FormData()
    formData.append('base64', base64)

    const response = await Http.post(url, formData)
    return response.link
  }

  async uploadImage(file: File): Promise<string> {
    if (!file) return ''
    const url = `https://webhook.silverybrand.com/api/upload-file`
    const formData = new FormData()
    formData.append('file', file)

    const response = await Http.post(url, formData)
    return response.link
  }

  async uploadAudio(file: File): Promise<string> {
    if (!file) return ''
    const url = `https://backend.fiverprints.com/api/spotify/upload-audio`
    const formData = new FormData()
    formData.append('file', file)

    const response = await Http.post(url, formData)
    return response.data?.url
  }

  async uploadImageTransferDefault({
    file,
    id,
  }: {
    file: File
    id: number | string
  }): Promise<{ image: string; success: boolean; message: string }> {
    if (!file || !id)
      return {
        image: '',
        success: false,
        message: '',
      }
    const url = `https://backend.fiverprints.com/api/style_transfer/default`
    const formData = new FormData()
    formData.append('image', file)
    formData.append('id', id.toString())

    const response = await Http.post(url, formData)

    return {
      image: response.data.image,
      success: response.success,
      message: response.message,
    }
  }

  async transferImage({
    image,
    templateId,
    id,
    custom_name,
  }: {
    image: string
    templateId: string
    id: string | number
    custom_name: string
  }): Promise<{
    url: string
    width: string
    height: string
    size: string
  }> {
    const url = `https://backend.fiverprints.com/api/style_transfer`
    const formData = new FormData()
    formData.append('image', image)
    formData.append('template_id', templateId)
    formData.append('id', id.toString())
    formData.append('custom_name', custom_name)

    const response = await Http.post(url, formData)
    return response.data?.url
  }

  async getCustomizationTemplates(payload: {
    code: string
    type: string
    tag: string
  }): Promise<{
    id: number
    option_sort_id: string
    json_data: {
      title: string
      image: string
      template_id: string
    }[]
  }> {
    const url = `https://backend.fiverprints.com/api/style_transfer/photo_editor_template?${buildQueryString(
      payload
    )}`
    const response = await Http.get(url)
    return response.data
  }

  async faceSwap({
    face_index,
    face_file,
    source_file,
  }: {
    face_index: number
    face_file: File
    source_file: File
  }): Promise<any> {
    if (!face_file || !source_file)
      return {
        image: '',
        success: false,
        message: '',
      }
    const url = `https://backend.fiverprints.com/api/face_swap`

    const formData = new FormData()
    formData.append('face_index', face_index.toString())
    formData.append('face_file', face_file)
    formData.append('source_file', source_file)

    const response = await Http.post(url, formData)

    return {
      image: response.data.image,
      face_image: response.data.face_image,
      success: response.success,
      message: response.message,
    }
  }

  async searchSong({
    songName,
    appName,
    nextPage,
  }: {
    songName?: string
    appName?: string
    nextPage?: string
  }): Promise<any> {
    const payload = { q: songName, app_name: appName }
    const url = nextPage
      ? nextPage
      : `https://backend.fiverprints.com/api/spotify/search?${buildQueryString(
          payload
        )}`

    const response = await Http.get(url)
    return response.data?.tracks?.map((item: any) => ({
      name: item.name,
      artist: item.artists?.name || item.artists[0].name,
      image: item.image.url,
      url: item.preview_url,
      uri: item.uri,
      nextPage: response.data.next,
    }))
  }

  async generateQrCodeByFile(
    file: File,
    color: string
  ): Promise<{ image: string; success: boolean; message: string }> {
    if (!file || !color)
      return {
        image: '',
        success: false,
        message: '',
      }
    const url = `https://backend.fiverprints.com/api/spotify/upload-file`
    const formData = new FormData()
    formData.append('file', file)
    formData.append('color', color)

    const response = await Http.post(url, formData)

    return {
      image: response.data.image,
      success: response.success,
      message: response.message,
    }
  }

  async generateQrCodeByUri(uri: string, color: string): Promise<any> {
    if (!uri || !color)
      return {
        image: '',
        success: false,
        message: '',
      }
    const url = `https://backend.fiverprints.com/api/spotify/generate-qr-code`

    const formData = new FormData()
    formData.append('uri', uri)
    formData.append('color', color)

    const response = await Http.post(url, formData)

    return {
      image: response.data.image,
      success: response.success,
      message: response.message,
    }
  }

  async generateWaveForm({
    type,
    file,
    url,
  }: {
    type: string
    file?: File
    url?: string
  }): Promise<any> {
    if (!type || (!file && !url))
      return {
        data: '',
        success: false,
        message: '',
      }

    const formData = new FormData()
    formData.append('type', type)

    if (file) {
      formData.append('file', file)
    }
    if (url) {
      formData.append('url', url)
    }

    const response = await Http.post(
      `https://backend.fiverprints.com/api/spotify/generate-wave-form`,
      formData
    )

    return {
      data: response.data,
      success: response.success,
      message: response.message,
    }
  }

  getProductDescription(payload: {
    code: string
    type?: string
    tag?: string
  }): Promise<ProductDescription> {
    const url = `https://backend.fiverprints.com/api/product/description?${buildQueryString(
      payload
    )}`
    return Http.get(url, {
      Authorization: APIAuthorization,
    })
  }

  getProductShipping(payload: {
    product_type: string
    delivery_country_code?: string
  }): Promise<ProductShipping> {
    const url = `https://backend.fiverprints.com/api/product/shipping-estimation?${buildQueryString(
      payload
    )}`
    return Http.get(url, {
      Authorization: APIAuthorization,
    })
  }

  getProductShippingBySku(payload: {
    sku_prefix: string
    full_sku: string
    vendor: string
    delivery_country_code?: string
  }): Promise<ProductShipping> {
    const url = `https://backend.fiverprints.com/api/product/shipping-estimation-by-sku?${buildQueryString(
      payload
    )}`
    return Http.get(url, {
      Authorization: APIAuthorization,
    })
  }

  async getProductRecommendationV2(
    payload: Recommendation
  ): Promise<Product[]> {
    const url = `https://webhook.silverybrand.com/api/recommendation/v2/product-page`
    const response = await Http.post(url, JSON.stringify(payload))

    return response.map((elm: any) =>
      mappingProductRecommendationResultToProduct(elm)
    )
  }

  async getProductRecommendation(payload: Recommendation) {
    const url = `https://webhook.silverybrand.com/api/recommendation/v3/product-page`
    const response = await Http.post(url, JSON.stringify(payload))

    return {
      moreItems: response?.more_items?.map(
        (item: ProductRecommendationResult) =>
          mappingProductRecommendationResultToProduct(item)
      ),
      similarItems: response?.similar_items?.map(
        (item: ProductRecommendationResult) =>
          mappingProductRecommendationResultToProduct(item)
      ),
      nonNicheItems: response?.non_niche_items?.map(
        (item: ProductRecommendationResult) =>
          mappingProductRecommendationResultToProduct(item)
      ),
      nicheItems: response?.niche_items?.map(
        (item: ProductRecommendationResult) =>
          mappingProductRecommendationResultToProduct(item)
      ),
      fatherWeed: response?.father_weed?.map(
        (item: ProductRecommendationResult) =>
          mappingProductRecommendationResultToProduct(item)
      ),
      motherWeed: response?.mother_weed?.map(
        (item: ProductRecommendationResult) =>
          mappingProductRecommendationResultToProduct(item)
      ),
      coupleWeed: response?.couple_weed?.map(
        (item: ProductRecommendationResult) =>
          mappingProductRecommendationResultToProduct(item)
      ),
      productMass: response?.product_mass?.map(
        (item: ProductRecommendationResult) =>
          mappingProductRecommendationResultToProduct(item)
      ),
    }
  }

  async getProductRecentlyViewedItems(payload: {
    recently_view: string[]
  }): Promise<Product[]> {
    const url = `https://webhook.silverybrand.com/api/recently-view/detail`
    const response = await Http.post(url, JSON.stringify(payload))

    return response.map((elm: any) =>
      mappingProductRecommendationResultToProduct(elm)
    )
  }

  async getProductSameDesign(
    handle: string,
    store: string
  ): Promise<
    { handle: string; product_type: string; sales: string | number }[]
  > {
    const url = `https://webhook.silverybrand.com/api/recommendation/same-design`
    const response = await Http.post(url, JSON.stringify({ handle, store }))
    return response
  }

  async getProductsRecommendationTrackingPage(payload: {
    ip: string
    email: string
    order: string
  }): Promise<Product[]> {
    const url = `https://webhook.silverybrand.com/api/recommendation/tracking-page?${buildQueryString(
      payload
    )}`

    const response = await Http.get(url)

    return response.map((elm: any) =>
      mappingProductRecommendationResultToProduct(elm)
    )
  }

  async getProductReviews(
    payload: GetProductReviewsParams
  ): Promise<JudgeMeGetProductReviewsResult> {
    const params: JudgeMeGetProductReviewsParams = {
      url: import.meta.env.VITE_SHOPIFY_DOMAIN as string,
      shop_domain: import.meta.env.VITE_SHOPIFY_DOMAIN as string,
      platform: 'shopify',
      per_page: payload.perPage || 5,
      page: payload.page || 1,
      product_id: payload.productId,
      sort_by: payload.sortBy || '',
      sort_dir: payload.sortDir || '',
      ts: Math.floor(Date.now() / 1000),
    }
    const url = `https://cdn.judge.me/reviews/reviews_for_widget?${buildQueryString(
      params
    )}`
    const response: JudgeMeGetProductReviewsResult = await Http.get(url)
    return response
  }

  async getJudgemePrerequisiteRequestForUpload() {
    const url = `https://judge.me/api/v1/pictures/presigned_data?url=${SHOP_DOMAIN}&shop_domain=${SHOP_DOMAIN}&platform=shopify`
    const response: JudgemePrerequisiteRequestForUploadResponse =
      await Http.get(url)
    return response
  }

  async jugdemeUploadImage(file: File) {
    const prerequisiteRequest =
      await this.getJudgemePrerequisiteRequestForUpload()
    const url = prerequisiteRequest.url
    const key = prerequisiteRequest.key_prefix + file.name

    const formData = new FormData()
    formData.append('key', key)
    for (const objectKey in prerequisiteRequest.fields) {
      if (
        Object.prototype.hasOwnProperty.call(
          prerequisiteRequest.fields,
          objectKey
        )
      ) {
        const element: any = prerequisiteRequest.fields[
          objectKey as keyof JudgemePrerequisiteRequestForUploadResponse
        ] as any
        formData.append(objectKey, element)
      }
    }
    formData.append('file', file)

    await Http.post(url, formData)
    return url + '/' + key
  }

  async judgemeSubmitReview(formData: FormData) {
    formData.append('shop_domain', SHOP_DOMAIN)
    formData.append('platform', PLATFORM)

    const url = 'https://judge.me/api/v1/reviews'
    return Http.post(url, formData)
  }

  async judgemeGetCustomQuestion(
    payload: JudgemeGetCustomQuestionInput
  ): Promise<JudgemeCustomFormQuestionResponse> {
    const params = {
      url: SHOP_DOMAIN,
      platform: PLATFORM,
      shop_domain: SHOP_DOMAIN,
      product_id: payload.productId,
    }
    const url = `https://judge.me/api/cf_answers/form_data_for_widget?${buildQueryString(
      params
    )}`
    return Http.get(url)
  }

  async getStatusOrderNumber({
    email,
    orderNumber,
  }: RequestTracking): Promise<any> {
    const params = {
      email,
      order_number: orderNumber,
    }
    const url = `https://webhook.silverybrand.com/api/order/tracking-status?${buildQueryString(
      params
    )}`
    return Http.get(url)
  }

  async subscribeUserToListKlaviyo({
    listId,
    email,
  }: {
    listId?: string
    email: string
  }) {
    const headers = {
      'content-type': 'application/json',
      revision: import.meta.env.VITE_REVISION_KLAVIYO,
    }
    const params = {
      company_id: import.meta.env.VITE_COMPANY_ID_KLAVIYO,
    }
    const data = {
      type: 'subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
            },
          },
        },
      },
      relationships: {
        list: {
          data: {
            type: 'list',
            id: listId || import.meta.env.VITE_LIST_ID_KLAVIYO,
          },
        },
      },
    }
    const url = `https://a.klaviyo.com/client/subscriptions/?${buildQueryString(
      params
    )}`

    await Http.post(url, JSON.stringify({ data }), headers)
  }

  async checkUrlBlogGreetingCard({ url }: { url: string }): Promise<any> {
    const params = {
      url,
    }
    const apiUrl = `https://webhook.silverybrand.com/api/greeting-card/url-check?${buildQueryString(
      params
    )}`
    return Http.get(apiUrl)
  }

  async getBlogGreetingCard({ url }: { url: string }): Promise<any> {
    const params = {
      url,
    }
    const apiUrl = `https://webhook.silverybrand.com/api/greeting-card/blog-content?${buildQueryString(
      params
    )}`
    const response = await Http.get(apiUrl)

    if (response.status) {
      return response
    }
    return
  }

  async getKolCampaign(code: string): Promise<any> {
    const params = {
      code,
    }
    const url = `https://webhook.silverybrand.com/api/kol-campaign/get-campaign?${buildQueryString(
      params
    )}`
    const response = await Http.get(url)

    if (response.status) {
      return {
        code: response.campaigns.code,
        name: response.campaigns.name,
        totalAmountPercentage: response.campaigns.total_amount_percentage,
        donateConfiguration: JSON.parse(
          response.campaigns.donate_configuration
        ),
        description: response.campaigns.description,
        blogUrl: response.campaigns.blog_url,
      }
    }
    return
  }

  async getKolAllCampaign(): Promise<any> {
    const url = `https://webhook.silverybrand.com/api/kol-campaign/get-all-campaign`
    const response = await Http.get(url)

    if (response.status) {
      return response.campaigns
    }
    return
  }

  async getKolCampaignTracking({
    email,
    orderName,
  }: RequestKolCampaignTracking): Promise<any> {
    const params = {
      email,
      order_name: orderName,
    }
    const url = `https://webhook.silverybrand.com/api/kol-campaign/get-kol-campaign-tracking?${buildQueryString(
      params
    )}`

    const response = await Http.get(url)

    if (response.status) {
      return {
        customer: response.customer,
        all: response.all,
        totalCount: response.count,
        sumAmount: response.sum,
      }
    }
    return
  }

  async getAllKolCampaignTrackingByCode(campaignCode: string): Promise<any> {
    const params = {
      campaign_code: campaignCode,
    }
    const url = `https://webhook.silverybrand.com/api/kol-campaign/get-all-campaign-tracking-by-code?${buildQueryString(
      params
    )}`

    const response = await Http.get(url)

    if (response.status) {
      return {
        all: response.all,
        totalCount: response.count,
        sumAmount: response.sum,
      }
    }
    return
  }

  async getCashOutByCode(campaignCode: string): Promise<any> {
    const params = {
      campaign_code: campaignCode,
    }
    const url = `https://webhook.silverybrand.com/api/kol-campaign/get-cash-out-by-campaign?${buildQueryString(
      params
    )}`

    const response = await Http.get(url)

    if (response.status) {
      return {
        all: response.all,
        totalCount: response.count,
        sumAmount: response.sum,
      }
    }
    return
  }

  async uploadAIImage(file: File): Promise<string> {
    if (!file) return ''
    const url = `https://backend.fiverprints.com/api/ai-image/upload`
    const formData = new FormData()
    formData.append('image', file)

    const response = await Http.post(url, formData)
    return response?.name
  }

  async transferAIImage({
    image,
    style,
    accessToken,
  }: {
    image: string
    style: string
    accessToken: string
  }) {
    if (!image || !style)
      return {
        image: '',
        success: false,
        message: '',
      }

    const data = new URLSearchParams({
      file_name: image,
      style,
      custome_access_token: accessToken,
    })

    const url = `https://backend.fiverprints.com/api/ai-image/transfer`
    const response = await Http.post(url, data)

    return {
      image: response.data?.image,
      success: response.success,
      message: response.message,
    }
  }

  async yourOwnDesign(payload: {
    product_id: bigint
    product_handle: string
    variant_title: string
    quantity: bigint
    customer_photos: []
    customer_email: string
    customer_idea: string
    customer_shipping_address: string
    customer_phone_number: string
    customer_name: string
  }) {
    const formData = new FormData()
    for (const key in payload) {
      formData.append(key, payload[key])
    }
    const url = `https://backend.fiverprints.com/api/own-product`
    const response = await Http.post(url, formData)

    return {
      success: response.success,
      message: response.message,
    }
  }

  async specialCustomerRegistration(payload, files) {
    const formData = new FormData()
    for (const key in payload) {
      formData.append(key, payload[key])
    }

    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        formData.append('images[]', files[i].file)
      }
    }
    const url = `https://backend.fiverprints.com/api/customers`
    const response = await Http.post(url, formData)

    return {
      success: response.success,
      message: response.message,
    }
  }

  async dowloadArtwork(productId, customerAccessToken): Promise<string> {
    if (!productId) return ''
    const url = `https://webhook.silverybrand.com/api/digital-product`
    const formData = new FormData()
    formData.append('product_id', productId)
    formData.append('customer_access_token', customerAccessToken)

    const response = await Http.post(url, formData)
    return response
  }

  async updateCartLineItemShopifyTheme(id: string, quantity: number) {
    if (!id) return
    const domain = window.location.hostname
    const url = `https://customize.${domain}/cart/change.js`
    try {
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Referer: `https://customize.${domain}`,
        },
        body: JSON.stringify({
          id,
          quantity,
        }),
      })
    } catch (error) {
      console.log(error)
    }
  }

  async identifyUser(ip?: string, email?: string, userAgent?: string) {
    const url = `https://webhook.silverybrand.com/api/identify`
    const formData = new FormData()
    if (ip) {
      formData.append('ip', ip)
    }
    if (email) {
      formData.append('email', email)
    }
    if (userAgent) {
      formData.append('userAgent', userAgent)
    }
    const response = await Http.post(url, formData)
    return response
  }

  async getUserKlaviyoByExchangeId(exchangeId: string, store: string) {
    const url = `https://webhook.silverybrand.com/api/email-by-exchange`
    const formData = new FormData()
    if (exchangeId) {
      formData.append('exchange_id', exchangeId)
    }
    if (store) {
      formData.append('store', store)
    }
    const response = await Http.post(url, formData)
    return response
  }

  async crossWordsCustomPublic(payload: string[]) {
    const url = `https://backend.fiverprints.com/api/style_transfer/crossword`
    const data = {
      data: {
        words: payload.map((word) => ({ word: word, clue: '' })),
      },
    }
    try {
      const response = await Http.post(url, JSON.stringify(data))
      return response
    } catch (error) {
      throw new Error(error)
    }
  }

  async getLettersPositionCustomilyLibrary(libraryId: number) {
    const url = `https://app.customily.com/api/Libraries/${libraryId}/Elements/Positions`
    try {
      const response = await Http.get(url)
      return response
    } catch (error) {
      throw new Error(error)
    }
  }

  async getLetterImageCustomilyLibrary(
    libraryId: number,
    positionLetter: number
  ) {
    const url =
      'https://backend.fiverprints.com/api/style_transfer/convert_image_base64'
    try {
      const formData = new FormData()
      formData.append('position', positionLetter)
      formData.append('library_id', libraryId)
      const response = await Http.post(url, formData)

      return {
        position: response?.position,
        sort: response?.Sort,
        image: `https://backend.fiverprints.com/storage/${response.BackendPath}`,
      }
    } catch (error) {
      throw new Error(error)
    }
  }

  async getOrderNumberByEmail(email: string, firstName: string) {
    const url = 'https://backend.fiverprints.com/api/customer-order-number'
    const store = import.meta.env.VITE_SHOPIFY_DOMAIN
    if (!email || !firstName || !store) return 0

    const formData = new FormData()
    formData.append('email', email)
    formData.append('first_name', firstName)
    formData.append('store', store)
    try {
      const response = await Http.post(url, formData)
      return response
    } catch (error) {
      return 0
    }
  }
}
